import { useEffect, useState } from "react";
import { Calendar, MapPin, Clock, CalendarPlus, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/Reveal";
import { Overline } from "@/components/Bits";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogDescription,
} from "@/components/ui/dialog";

function fmtDate(iso) {
  try {
    return new Date(iso + "T00:00:00").toLocaleDateString(undefined, { weekday: "short", month: "short", day: "numeric", year: "numeric" });
  } catch { return iso; }
}
function dayNum(iso) { try { return new Date(iso + "T00:00:00").getDate(); } catch { return "--"; } }
function monthAbbr(iso) { try { return new Date(iso + "T00:00:00").toLocaleDateString(undefined, { month: "short" }).toUpperCase(); } catch { return ""; } }

function addToCalendar(ev) {
  const d = ev.date.replace(/-/g, "");
  const ics = [
    "BEGIN:VCALENDAR", "VERSION:2.0", "PRODID:-//ApostleBlaiseJudah//Events//EN",
    "BEGIN:VEVENT", `UID:${ev.id}@apostleblaisejudah.com`,
    `DTSTART;VALUE=DATE:${d}`, `SUMMARY:${ev.title}`,
    `DESCRIPTION:${(ev.description || "").replace(/\n/g, " ")}`,
    `LOCATION:${ev.location || ""}`, "END:VEVENT", "END:VCALENDAR",
  ].join("\r\n");
  const blob = new Blob([ics], { type: "text/calendar;charset=utf-8" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  a.download = `${ev.title.replace(/\s+/g, "_")}.ics`;
  document.body.appendChild(a); a.click(); a.remove();
  toast.success("Event saved to your calendar");
}

function RsvpDialog({ event }) {
  const [form, setForm] = useState({ name: "", email: "" });
  const [busy, setBusy] = useState(false);
  const [open, setOpen] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await api.post(`/events/${event.id}/rsvp`, form);
      toast.success("You're registered! We look forward to seeing you.");
      setForm({ name: "", email: "" });
      setOpen(false);
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail));
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <button data-testid={`rsvp-btn-${event.id}`} className="inline-flex items-center gap-2 bg-gold-500 px-5 py-2.5 text-xs font-semibold text-ink hover:bg-gold-400 transition-colors duration-300">
          Register <ArrowUpRight size={14} />
        </button>
      </DialogTrigger>
      <DialogContent className="bg-ink-paper border-gold-500/25 text-white">
        <DialogHeader>
          <DialogTitle className="font-serif text-2xl text-white">Register — {event.title}</DialogTitle>
          <DialogDescription className="text-white/55 text-sm">{fmtDate(event.date)} · {event.time}</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4 mt-2" data-testid="rsvp-form">
          <Input required placeholder="Full name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="rsvp-name" className="bg-ink border-gold-500/25 text-white placeholder:text-white/40" />
          <Input required type="email" placeholder="Email address" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="rsvp-email" className="bg-ink border-gold-500/25 text-white placeholder:text-white/40" />
          <Button type="submit" disabled={busy} data-testid="rsvp-submit" className="w-full bg-gold-500 text-ink hover:bg-gold-400 font-semibold">
            {busy ? "Registering…" : "Confirm Registration"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}

export default function Events() {
  const [events, setEvents] = useState([]);

  useEffect(() => {
    api.get("/events").then(({ data }) => setEvents(data)).catch(() => {});
  }, []);

  const featured = events.find((e) => e.featured) || events[0];
  const rest = events.filter((e) => e !== featured);

  return (
    <div data-testid="events-page">
      <PageHero
        overline="Upcoming Events"
        title={<>Gather. Grow. <span className="font-accent text-gold-gradient italic">Go further.</span></>}
        subtitle="Conferences, services, and trainings where leaders are equipped and lives are transformed. Plan to attend — or invite Apostle Blaise Judah to your city."
      />

      {/* Featured */}
      {featured && (
        <section className="mx-auto max-w-[1400px] px-6 md:px-10 py-20">
          <Reveal>
            <div className="grid gap-10 lg:grid-cols-2 lg:items-center border border-gold-500/25 corner-frame overflow-hidden" data-testid="featured-event">
              <div className="relative aspect-[4/3] lg:aspect-auto lg:h-full">
                <img src={featured.image} alt={featured.title} className="h-full w-full object-cover" />
                <div className="absolute inset-0 bg-gradient-to-r from-transparent to-ink/40" />
                <div className="absolute left-6 top-6 flex flex-col items-center justify-center bg-ink/90 border border-gold-500/40 h-20 w-20">
                  <span className="font-serif text-3xl text-gold-400 leading-none">{dayNum(featured.date)}</span>
                  <span className="text-[10px] tracking-[0.2em] text-white/70 mt-1">{monthAbbr(featured.date)}</span>
                </div>
              </div>
              <div className="p-8 lg:p-12">
                <span className="text-[10px] uppercase tracking-[0.3em] text-gold-400">Featured · {featured.type}</span>
                <h2 className="mt-3 font-serif text-3xl md:text-4xl text-white">{featured.title}</h2>
                <p className="mt-4 text-white/65 leading-relaxed">{featured.description}</p>
                <div className="mt-6 space-y-2 text-sm text-white/70">
                  <p className="flex items-center gap-2"><Calendar size={15} className="text-gold-500" /> {fmtDate(featured.date)}</p>
                  <p className="flex items-center gap-2"><Clock size={15} className="text-gold-500" /> {featured.time}</p>
                  <p className="flex items-center gap-2"><MapPin size={15} className="text-gold-500" /> {featured.location}</p>
                </div>
                <div className="mt-8 flex flex-wrap gap-3">
                  <RsvpDialog event={featured} />
                  <button onClick={() => addToCalendar(featured)} data-testid={`ics-${featured.id}`} className="inline-flex items-center gap-2 border border-gold-500/40 px-5 py-2.5 text-xs text-white/85 hover:border-gold-400 hover:text-gold-300 transition-colors">
                    <CalendarPlus size={14} /> Add to Calendar
                  </button>
                </div>
              </div>
            </div>
          </Reveal>
        </section>
      )}

      {/* Upcoming list */}
      <section className="mx-auto max-w-[1400px] px-6 md:px-10 pb-28">
        <Reveal className="mb-12"><Overline>All Upcoming</Overline></Reveal>
        <div className="grid gap-6 md:grid-cols-2 lg:grid-cols-3">
          {rest.map((ev, i) => (
            <Reveal key={ev.id} delay={(i % 3) * 0.06}>
              <div className="group flex h-full flex-col border border-gold-500/20 bg-ink-paper/40 hover:border-gold-400/60 transition-colors duration-500" data-testid={`event-card-${ev.id}`}>
                <div className="relative overflow-hidden aspect-[16/9]">
                  <img src={ev.image} alt={ev.title} loading="lazy" className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                  <div className="absolute inset-0 bg-gradient-to-t from-ink to-transparent" />
                  <div className="absolute left-4 top-4 flex flex-col items-center justify-center bg-ink/85 border border-gold-500/40 h-16 w-16">
                    <span className="font-serif text-2xl text-gold-400 leading-none">{dayNum(ev.date)}</span>
                    <span className="text-[9px] tracking-[0.2em] text-white/70 mt-0.5">{monthAbbr(ev.date)}</span>
                  </div>
                  <span className="absolute right-3 top-3 bg-ink/80 px-3 py-1 text-[10px] uppercase tracking-[0.2em] text-gold-400 border border-gold-500/30">{ev.type}</span>
                </div>
                <div className="flex flex-1 flex-col p-6">
                  <h3 className="font-serif text-xl text-white group-hover:text-gold-300 transition-colors">{ev.title}</h3>
                  <p className="mt-3 text-sm text-white/60 leading-relaxed flex-1">{ev.description}</p>
                  <div className="mt-4 space-y-1.5 text-xs text-white/55">
                    <p className="flex items-center gap-2"><Clock size={12} className="text-gold-500" /> {ev.time}</p>
                    <p className="flex items-center gap-2"><MapPin size={12} className="text-gold-500" /> {ev.location}</p>
                  </div>
                  <div className="mt-5 flex items-center gap-2 border-t border-gold-500/15 pt-5">
                    <RsvpDialog event={ev} />
                    <button onClick={() => addToCalendar(ev)} data-testid={`ics-${ev.id}`} className="inline-flex items-center gap-2 border border-gold-500/40 px-4 py-2.5 text-xs text-white/85 hover:border-gold-400 hover:text-gold-300 transition-colors">
                      <CalendarPlus size={13} />
                    </button>
                  </div>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </section>
    </div>
  );
}
